import { SearchableJobSortField } from 'jobs/dtos/job'
import SearchableJob from 'jobs/SearchableJob'
import { WaitingOn } from '@taxfyle/api-internal/internal/job_pb'
import { Resolution } from 'jobs/Job'
import { JobProgressionStepStatus } from 'jobs/progression/JobProgression'
import { JobScopeChangeStatus } from '@taxfyle/api-internal/internal/job_scope_change_pb'

/**
 * Column (row?) density presets.
 */
export enum ColumnDensity {
  Default,
  Comfortable,
  Compact,
}

/**
 * Unique identifiers for each column.
 */
export enum ColumnId {
  JobTitle = 'jobTitle',
  JobShortId = 'jobShortId',
  LegendName = 'legendName',
  Workspace = 'workspace',
  Status = 'status',
  ProgressionStep = 'progressionStep',
  JobScopeChangeRequestStatus = 'jobScopeChangeRequestStatus',
  Milestone = 'milestone',
  WaitingOn = 'waitingOn',
  ClientTeam = 'clientTeam',
  ProTeam = 'proTeam',
  ClientChampion = 'clientChampion',
  ProChampion = 'proChampion',
  IsRequestedPro = 'isRequestedPro',
  Payout = 'payout',
  Total = 'total',
  TotalPaid = 'totalPaid',
  Created = 'created',
  Submitted = 'submitted',
  Claimed = 'claimed',
  Deadline = 'deadline',
  Completed = 'completed',
  LastActivity = 'lastActivity',
  Action = 'action',
}

/**
 * Whether a column is common to all users or particular to clients, providers, or admins.
 * Admin users can view all column types.
 */
export enum ColumnType {
  Common = 'common',
  Client = 'client',
  Provider = 'provider',
  Admin = 'admin',
}

export interface StaticColumnDetails {
  id: ColumnId
  header: string
  modifyVisibility: boolean
  field?: SearchableJobSortField
  sortable: boolean
}

export interface MutableColumnDetails {
  id: string
  visible: boolean
}

/**
 * Static column details for populating the Job Management table.
 */
export const JobsTableColumns: StaticColumnDetails[] = [
  {
    id: ColumnId.JobTitle,
    header: 'Job title',
    modifyVisibility: false,
    field: 'Title',
    sortable: true,
  },
  {
    id: ColumnId.JobShortId,
    header: 'Job ID',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.LegendName,
    header: 'Job type',
    modifyVisibility: true,
    field: 'LegendName',
    sortable: true,
  },
  {
    id: ColumnId.Workspace,
    header: 'Workspace',
    modifyVisibility: true,
    field: 'WorkspaceName',
    sortable: true,
  },
  {
    id: ColumnId.Status,
    header: 'Status',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.ProgressionStep,
    header: 'Job progress',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.JobScopeChangeRequestStatus,
    header: 'Job scope updates',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.Milestone,
    header: 'Milestone',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.WaitingOn,
    header: 'Waiting on',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.ClientTeam,
    header: 'Client team',
    modifyVisibility: true,
    field: 'ClientTeamName',
    sortable: true,
  },
  {
    id: ColumnId.ProTeam,
    header: 'Pro team',
    modifyVisibility: true,
    field: 'ProTeamName',
    sortable: true,
  },
  {
    id: ColumnId.ClientChampion,
    header: 'Client champion',
    modifyVisibility: true,
    field: 'ClientChampionName',
    sortable: true,
  },
  {
    id: ColumnId.ProChampion,
    header: 'Pro champion',
    modifyVisibility: true,
    field: 'ProChampionName',
    sortable: true,
  },
  {
    id: ColumnId.IsRequestedPro,
    header: 'Is requested pro',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.Payout,
    header: 'Payout',
    modifyVisibility: true,
    field: 'ProviderCutAmount',
    sortable: true,
  },
  {
    id: ColumnId.Total,
    header: 'Total',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.TotalPaid,
    header: 'Total paid',
    modifyVisibility: true,
    sortable: false,
  },
  {
    id: ColumnId.Created,
    header: 'Created',
    modifyVisibility: true,
    field: 'DateCreated',
    sortable: true,
  },
  {
    id: ColumnId.Submitted,
    header: 'Submitted',
    modifyVisibility: true,
    field: 'DateSubmitted',
    sortable: true,
  },
  {
    id: ColumnId.Claimed,
    header: 'Claimed',
    modifyVisibility: true,
    field: 'DateClaimed',
    sortable: true,
  },
  {
    id: ColumnId.Deadline,
    header: 'Deadline',
    modifyVisibility: false,
    field: 'DateDeadline',
    sortable: true,
  },
  {
    id: ColumnId.Completed,
    header: 'Completed',
    modifyVisibility: true,
    field: 'DateCompleted',
    sortable: true,
  },
  {
    id: ColumnId.LastActivity,
    header: 'Last activity',
    modifyVisibility: false,
    field: 'DateLastActivity',
    sortable: true,
  },
]

/**
 * Returns the exported value for a jobs table column.
 */
export function exportValue(columnId: ColumnId, job: SearchableJob) {
  switch (columnId) {
    case ColumnId.JobTitle:
      return job.displayName
    case ColumnId.JobShortId:
      return job.shortId
    case ColumnId.LegendName:
      return job.legendName
    case ColumnId.Workspace:
      return job.workspace.name
    case ColumnId.Status:
      return formattedStatus(job)
    case ColumnId.ProgressionStep:
      return formattedProgressionStep(job)
    case ColumnId.JobScopeChangeRequestStatus:
      return formattedJobScopeChangeRequestStatus(job.scopeChangeStatus)
    case ColumnId.Milestone:
      return formattedCurrentMilestone(job)
    case ColumnId.WaitingOn:
      return formattedWaitingOn(job.waitingOn)
    case ColumnId.Payout:
      return job.formattedProviderCutAmount
    case ColumnId.Total:
      return job.formattedTotalPrice
    case ColumnId.ClientTeam:
      return job.clientTeamName
    case ColumnId.ProTeam:
      return job.proTeamName
    case ColumnId.ClientChampion:
      return job.championClient?.displayName
    case ColumnId.ProChampion:
      return job.championPro?.displayName
    case ColumnId.Created:
      return formattedDate(job.dateCreated)
    case ColumnId.Submitted:
      return formattedDate(job.dateSubmitted)
    case ColumnId.Claimed:
      return formattedDate(job.dateAccepted)
    case ColumnId.Deadline:
      return formattedDate(job.dateDeadline)
    case ColumnId.Completed:
      return formattedDate(job.dateClosed)
    case ColumnId.LastActivity:
      return formattedDate(job.dateLastActivity)
    default:
      return undefined
  }
}

/**
 * Returns the title of the current milestone plus the ratio of tasks completed.
 */
function formattedCurrentMilestone(job: SearchableJob) {
  const current = job.currentMilestone
  if (!current) {
    return undefined
  }
  const currentTitle = current.title
  const completedTasks = current.tasksList.filter((t) => t.completed).length
  const totalTasks = current.tasksList.length

  return `${currentTitle} (${completedTasks}/${totalTasks})`
}

/**
 * Returns the formatted status.
 */
function formattedStatus(job: SearchableJob) {
  const withResolution = () => {
    switch (job.resolution) {
      case Resolution.Cancelled:
        return 'Closed (cancelled)'
      case Resolution.Test:
        return 'Closed (test)'
      default:
        return 'Closed'
    }
  }

  switch (job.status) {
    case 'UNDER_CONSTRUCTION':
      return 'Onboarding'
    case 'INFO_GATHERING':
      return 'Info Gathering'
    case 'UNCLAIMED':
      return 'Unclaimed'
    case 'CLAIMED':
      return 'In Progress'
    case 'CLOSED':
      return withResolution()
    case 'IDLE':
      return 'Idle'
    case 'ON_HOLD':
      return 'On Hold'
  }
}

/**
 * Returns the formatted progression step.
 */
export function formattedProgressionStep(job: SearchableJob) {
  switch (job.progressionStep) {
    case JobProgressionStepStatus.OPEN_ITEMS:
      return 'Open items'
    case JobProgressionStepStatus.JOB_STARTED:
      return 'Job started'
    case JobProgressionStepStatus.DRAFT_IN_REVIEW:
      return 'Draft review'
    case JobProgressionStepStatus.DRAFT_APPROVED:
      return 'Draft approved'
    case JobProgressionStepStatus.DRAFT_REJECTED:
      return 'Changes requested'
    case JobProgressionStepStatus.AUTHORIZATION_REQUESTED:
      return 'Authorization requested'
    case JobProgressionStepStatus.AUTHORIZATION_SIGNED:
      return 'Authorization received'
    case JobProgressionStepStatus.RETURN_FILED:
      return 'Return filed'
    case JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED:
      return ''
    case JobProgressionStepStatus.NOT_STARTED:
      return 'Review documents'
    default:
      return ''
  }
}

/**
 * Returns a formatted date.
 */
function formattedDate(date: Date | null) {
  return date?.toLocaleString()
}

/**
 * Returns the formatted text value for waiting on.
 */
export function formattedWaitingOn(waitingOn: WaitingOn | null) {
  switch (waitingOn) {
    case WaitingOn.WAITING_ON_CLIENT:
      return 'Client'
    case WaitingOn.WAITING_ON_PRO:
      return 'Pro'
    default:
      return undefined
  }
}

export function formattedJobScopeChangeRequestStatus(
  scopeChangeRequestStatus: JobScopeChangeStatus
) {
  switch (scopeChangeRequestStatus) {
    case JobScopeChangeStatus.UNSPECIFIED:
      return undefined
    case JobScopeChangeStatus.ACCEPTED:
      return 'Accepted'
    case JobScopeChangeStatus.REJECTED:
      return 'Rejected'
    case JobScopeChangeStatus.PENDINGADMINAPPROVAL:
    case JobScopeChangeStatus.PENDINGFINALIZATION:
    case JobScopeChangeStatus.PENDINGCLIENTAPPROVAL:
      return 'Pending review'
    default:
      return undefined
  }
}
